/**
 * Entry point: wires Game + UI + Audio, installs QA hooks, shows the title
 * (or jumps straight into a stage when URL params ask for it).
 */
import { AudioEngine } from '@audio/AudioEngine';
import { Game, reloadForNewBuild } from '@core/Game';
import { applyUrlParams, installDebug } from '@core/debug';
import { UI } from '@ui/UI';

async function boot(): Promise<void> {
  const canvas = document.getElementById('game') as HTMLCanvasElement;
  const uiRoot = document.getElementById('ui') as HTMLElement;
  const params = new URLSearchParams(location.search);
  if (params.has('viewer')) {
    // Art-pipeline model viewer (src/fx/viewer.ts) — stage-free GLB preview.
    document.getElementById('boot')?.remove();
    await (await import('./fx/viewer')).mountViewer(canvas, params);
    return;
  }
  const audio = new AudioEngine();
  const game = new Game(canvas, audio, params.get('quality'));
  await game.init();
  const ui = new UI(game, uiRoot);
  game.attachUi(ui);
  game.applySettings();
  const api = installDebug(game);
  game.start();
  document.getElementById('boot')?.remove();
  // Vite fires this when a lazily-imported chunk's preload fails (stale tab after a deploy).
  window.addEventListener('vite:preloadError', (e) => {
    if (reloadForNewBuild(game.run)) e.preventDefault();
  });
  if (params.get('rtest') === '1') {
    const { startRenderTest } = await import('@core/rendertest');
    startRenderTest(game);
    return;
  }
  const direct = await applyUrlParams(game, api);
  if (!direct) game.showTitle();
}

boot().catch((err) => {
  console.error(err);
  const b = document.getElementById('boot');
  if (b) b.textContent = `Failed to start: ${err instanceof Error ? err.message : String(err)}`;
});
